import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FolderLock, Plus, Copy, Trash2, ExternalLink, Loader2, Check } from "lucide-react";
import { api, apiError } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, EmptyState, money, shortDate } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

export default function ProofPacks() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const { data: packs = [], isLoading } = useQuery({
    queryKey: ["packs"],
    queryFn: async () => (await api.get("/proof-packs")).data,
  });
  const { data: receipts = [] } = useQuery({
    queryKey: ["mine"],
    queryFn: async () => (await api.get("/receipts/mine")).data,
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    if (!name || selected.length === 0) return toast.error("Name your pack and pick at least one receipt.");
    setBusy(true);
    try {
      await api.post("/proof-packs", { name, receipt_ids: selected });
      toast.success("Proof Pack created");
      setOpen(false); setName(""); setSelected([]);
      qc.invalidateQueries({ queryKey: ["packs"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/proof-packs/${id}`);
    qc.invalidateQueries({ queryKey: ["packs"] });
    toast.success("Proof Pack deleted");
  };

  const copyLink = (pack) => {
    const url = `${window.location.origin}/p/${pack.share_token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(pack.id); setTimeout(() => setCopiedId(null), 1500);
    toast.success("Share link copied");
  };

  return (
    <AppLayout>
      <PageHeader eyebrow="Bundles" title="Proof Packs" subtitle="Bundle receipts into a shareable pack for insurance, resale or tax time."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand text-white hover:bg-brand-hover" data-testid="new-pack-btn"><Plus className="mr-2 h-4 w-4" />New pack</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="new-pack-dialog">
              <DialogHeader><DialogTitle className="font-heading">Create a Proof Pack</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pname">Pack name</Label>
                  <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Home office equipment" className="mt-1.5" data-testid="pack-name-input" />
                </div>
                <div>
                  <Label>Select receipts</Label>
                  <div className="mt-1.5 max-h-64 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
                    {receipts.length === 0 && <p className="p-3 text-sm text-muted-foreground">No receipts to add yet.</p>}
                    {receipts.map((r) => (
                      <label key={r.id} className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-accent" data-testid={`pack-receipt-${r.id}`}>
                        <Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
                        <span className="flex-1 text-sm font-medium">{r.items?.[0]?.name || "Purchase"}</span>
                        <span className="font-mono text-sm">{money(r.total)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button onClick={create} disabled={busy} className="w-full bg-brand text-white hover:bg-brand-hover" data-testid="create-pack-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create pack (${selected.length})`}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : packs.length === 0 ? (
        <EmptyState icon={FolderLock} title="No Proof Packs yet" subtitle="Group receipts together and share them with a secure link." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {packs.map((p) => (
            <div key={p.id} className="flex flex-col rounded-2xl border border-border bg-card p-6" data-testid={`pack-${p.id}`}>
              <div className="mb-3 flex items-start justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-muted text-brand"><FolderLock className="h-6 w-6" /></span>
                <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-pack-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <h3 className="font-heading text-lg font-extrabold">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.count} receipt(s) · {money(p.total_value)}</p>
              <p className="mt-1 text-xs text-muted-foreground">Created {shortDate(p.created_at)}</p>
              <div className="mt-auto flex gap-2 pt-4">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => copyLink(p)} data-testid={`copy-link-${p.id}`}>
                  {copiedId === p.id ? <Check className="mr-1 h-4 w-4 text-success" /> : <Copy className="mr-1 h-4 w-4" />}Copy link
                </Button>
                <a href={`/p/${p.share_token}`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" data-testid={`open-pack-${p.id}`}><ExternalLink className="h-4 w-4" /></Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
